import { logError } from '../utils/logError';
import UrlQueryParams from '../url-query-params';
import { showMessage } from '../utils/showMessage';

/**
 * Like and Dislike functionality
 */
class Likes {
    static ACTION_LIKE = 1;
    static ACTION_DISLIKE = -1;
    static ACTION_UNSET = 0;
    static CORRECTION_PREUPDATE = 1;
    static CORRECTION_CANCEL_PREUPDATE = -1;
    static CLASS_USER_HAS_VOTED = 'has-voted';

    /** @var {HTMLElement} */
    container;

    /** @var {HTMLElement[]} */
    buttons = [];

    /** @var {Object} */
    listeners = {};

    /** @var {string} */
    reviewId;

    /** @var {boolean} */
    hasUserVoted = false;

    /**
     * @param {HTMLElement} element
     * @param {string} reviewId
     */
    constructor (element, reviewId) {
        if (!(element instanceof HTMLElement))
            throw new Error('HTMLElement is expected as constructor argument of Likes::class');

        if (!reviewId)
            throw new Error('Review id is expected');

        this.listeners.onLikeChange = this.onLikeChange.bind(this);

        this.container = element;
        this.buttons = [...this.container.querySelectorAll('.likes, .dislikes')];
        this.hasUserVoted = this.container.querySelector('.has-voted') instanceof HTMLElement;
        this.reviewId = reviewId;

        this.attachEventListeners()
    }

    attachEventListeners () {
        for (const btn of this.buttons)
            btn.addEventListener('click', this.listeners.onLikeChange);
    }

    /**
     * Changes like or dislike
     * @param {MouseEvent|TouchEvent|PointerEvent} event
     * @return {void}
     */
    async onLikeChange (event) {
        event.preventDefault();
        const clickedBtn = event.currentTarget;

        if (clickedBtn.hasAttribute('disabled'))
            return;

        let action;
        const isClickedOnVotedButton = clickedBtn.classList.contains(Likes.CLASS_USER_HAS_VOTED);
        const isClickedOnLikeButton = clickedBtn.classList.contains('likes');

        if (isClickedOnVotedButton)
            action = Likes.ACTION_UNSET;
        else if (isClickedOnLikeButton)
            action = Likes.ACTION_LIKE;
        else
            action = Likes.ACTION_DISLIKE;

        // Preupdate buttons to estimated state for visually instant response
        this.updateButtons(
            action,
            this.getCurrentLikeDislikeCount(this.getLikeDislikeCorrection({
                action,
                isClickedOnLikeButton
            }, Likes.CORRECTION_PREUPDATE))
        );

        // Block buttons unless server responds
        for (const btn of this.buttons)
            btn.setAttribute('disabled', 'disabled');

        try {
            // Wait for server response
            const response = await this.sendLike(action);
            this.updateButtons(action, response);
            this.hasUserVoted = 0 !== action;
        } catch (errorResponse) {
            // Restore previous state of buttons
            this.updateButtons(
                this.hasUserVoted
                    ? (isClickedOnLikeButton ? Likes.ACTION_LIKE : Likes.ACTION_DISLIKE)
                    : Likes.ACTION_UNSET,
                this.getCurrentLikeDislikeCount(this.getLikeDislikeCorrection({
                    action,
                    isClickedOnLikeButton
                }, Likes.CORRECTION_CANCEL_PREUPDATE))
            );

            console.error(errorResponse.responseJSON || errorResponse.responseText);
            logError(errorResponse.responseJSON || errorResponse.responseText);
        } finally {
            // Unlock buttons
            for (const btn of this.buttons)
                btn.removeAttribute('disabled');
        }
    }

    /**
     * Sends request to change like to the server
     * @param {number} action a like code, one of [-1, 0, 1] to [dislike, unset, like] respectively
     * @return {Promise<{ likeCount: number, dislikeCount: number }>}
     */
    sendLike (action) {
        return fetch('/api/prk/catalog/product/review/like/', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({
                review_id: this.reviewId,
                like: action
            }),
        })
            .then(response => response.json())
            .then(response => {
                // Transform from snake_case to camelCase
                return {
                    likeCount: response.like_count,
                    dislikeCount: response.dislike_count
                }
            })
            .catch(logError);
    }

    /**
     * Updates state of buttons
     * @param {number} action a code of [-1. 0, 1] to [dislike, unset, like] respectively
     * @param {{ likeCount: number, dislikeCount: number }} count
     */
    updateButtons (action, count) {
        for (const btn of this.buttons) {
            const isLikeBtn = btn.classList.contains('likes');

            // Update CLASS_USER_HAS_VOTED on buttons
            switch (action) {
                case Likes.ACTION_DISLIKE:
                    btn.classList[isLikeBtn ? 'remove' : 'add'](Likes.CLASS_USER_HAS_VOTED);
                    break;

                case Likes.ACTION_UNSET:
                    btn.classList.remove(Likes.CLASS_USER_HAS_VOTED);
                    break;

                case Likes.ACTION_LIKE:
                    btn.classList[isLikeBtn ? 'add' : 'remove'](Likes.CLASS_USER_HAS_VOTED);
                    break;
            }

            // Update count of likes and dislikes
            const valueElement = btn.querySelector('.value');
            if (valueElement instanceof HTMLElement) {
                valueElement.innerText = isLikeBtn
                    ? count.likeCount
                    : count.dislikeCount
            }
        }
    }

    /**
     * Returns like and dislike count
     * @param {{ likeCount?: number, dislikeCount?: number }} correction
     * @return {{ likeCount: number, dislikeCount: number }}
     */
    getCurrentLikeDislikeCount (correction) {
        let { likeCount = 0, dislikeCount = 0 } = correction;
        likeCount += this.likeCount;
        dislikeCount += this.dislikeCount;
        return { likeCount, dislikeCount };
    }

    /**
     * Helper method that
     * setups a correction to likeCount and dislikeCount
     * to pass to Likes::getCurrentLikeDislikeCount.
     * Is used to preupdate values (or cancel preudate of values)
     * on click on 'like' or 'dislike'
     * before server responds, to make it visually instant.
     * @param {{ action, isClickedOnLikeButton }} state
     * @param { number } correction
     */
    getLikeDislikeCorrection ({ action, isClickedOnLikeButton }, correction) {
        let likeCount = 0,
            dislikeCount = 0;

        switch (action) {
            case Likes.ACTION_LIKE:
                likeCount = correction;
                if (this.hasUserVoted)
                    dislikeCount = -correction;
                break;

            case Likes.ACTION_DISLIKE:
                dislikeCount = correction;
                if (this.hasUserVoted)
                    likeCount = -correction;
                break;

            case Likes.ACTION_UNSET:
                if (isClickedOnLikeButton)
                    likeCount = -correction;
                else
                    dislikeCount = -correction
                break;
        }

        return { likeCount, dislikeCount };
    }

    /**
     * Getter returns like count
     * @return {number}
     */
    get likeCount () {
        if (!this.buttons.length) return 0;
        const valueElement = this.buttons.filter(button => button.classList.contains('likes'))[0]
            .querySelector('.value');
        if (!valueElement) return 0;
        return parseInt(valueElement.innerText);
    }

    /**
     * Getter returns dislike count
     * @return {number}
     */
    get dislikeCount () {
        if (!this.buttons.length) return 0;
        const valueElement = this.buttons.filter(button => button.classList.contains('dislikes'))[0]
            .querySelector('.value');
        if (!valueElement) return 0;
        return parseInt(valueElement.innerText);
    }

    /**
     * Getter returns like - dislike score
     * @return {number}
     */
    get score () {
        return this.likeCount - this.dislikeCount;
    }
}

/**
 * Review functionality
 */
class Review {
    /** @var {string} */
    id;

    /** @var {HTMLElement} */
    container;

    /** @var {Likes} */
    likes;

    /**
     * @param {HTMLElement} element
     */
    constructor (element) {
        if (!(element instanceof HTMLElement))
            throw new Error('HTMLElement is expected as constructor argument of Review::class');

        this.container = element;
        this.id = this.container.dataset.reviewId;
        this.likes = new Likes(this.container.querySelector('.likes-and-dislikes'), this.id);
    }

    /**
     * Getter returns like count of the review
     * @return {number}
     */
    get likeCount () {
        return this.likes.likeCount;
    }

    /**
     * Getter returns dislike count of the review
     * @return {number}
     */
    get dislikeCount () {
        return this.likes.dislikeCount;
    }

    /**
     * Getter returns score of the review based on likes and dislikes
     * @return {number}
     */
    get score () {
        return this.likes.score;
    }

    /**
     * Getter returns the date of the review acception
     * @return {string}
     */
    get date () {
        return this.container.dataset.dateCreated || '';
    }

    /**
     * Getter returns the review's rating
     */
    get rating () {
        const rating = this.container.querySelector('input.rating');
        return rating ? rating.value : '5';
    }

    /**
     * Getter returns true if the review has attached photos
     */
    get hasPhotos () {
        return !!this.container.dataset.hasPhotos;
    }

    /**
     * Shows the review
     */
    show () {
        this.container.classList.remove('review--is-hidden');
    }

    /**
     * Hides the review
     */
    hide () {
        this.container.classList.add('review--is-hidden');
    }
}

/**
 * Reviews block functionality
 */
export class Reviews {
    static DEFAULT_PER_PAGE_REVIEW_COUNT = 10;
    static API_FETCH_REVIEWS_URL = '/include/catalog/product/reviews/';

    /** @var {HTMLElement} */
    container;

    /** @var {HTMLElement|null} */
    reviewsContainer;

    /** @var {Review[]} */
    reviews = [];

    /** @var {Object} */
    listeners = {};

    /** @var {{ predicate: string|null, reverse: boolean }} */
    sort = {
        predicate: null,
        reverse: false
    };

    /** @var {number} */
    totalReviewCount;

    /** @var {number} */
    perPageReviewCount;

    /** @var {boolean} */
    shouldFilterReviewsWithPhotos = false;

    /** @private @var {boolean} */
    _isLoadingReviews = false;

    /** @private @var {string|null} */
    productId;

    /** @var {HTMLButtonElement|null} */
    loadNextReviewsButton;

    /** @var {number|null} */
    lastReviewsRequestLimit;

    /**
     * @param {HTMLElement} element
     */
    constructor (element) {
        if (!(element instanceof HTMLElement))
            throw new Error('HTMLElement is expected as constructor argument of Reviews::class');

        this.listeners.onSortReviews = this.onSortReviews.bind(this);
        this.listeners.onFilterHasPhotos = this.onFilterHasPhotos.bind(this);
        this.listeners.loadNextReviews = this.loadNextReviews.bind(this);

        this.container = element;
        this.reviewsContainer = this.container.querySelector('.reviews');

        // Init reviews
        for (const reviewElement of [...this.container.querySelectorAll('.review')]) {
            this.reviews.push(new Review(reviewElement));
        }
        this.totalReviewCount = this.reviewsContainer instanceof HTMLElement
            ? parseInt(this.reviewsContainer.dataset.totalReviewCount) || this.reviews.length
            : this.reviews.length;

        this.perPageReviewCount = this.reviewsContainer instanceof HTMLElement
            ? parseInt(this.reviewsContainer.dataset.perPageReviewCount) || Reviews.DEFAULT_PER_PAGE_REVIEW_COUNT
            : Reviews.DEFAULT_PER_PAGE_REVIEW_COUNT;

        this.productId = this.container.dataset.productId || null;
        this.loadNextReviewsButton = this.container.querySelector('.js-show-more-reviews');

        this.attachEventListeners();
    }

    /**
     * Returns private _isLoadingReviews
     * @returns {boolean}
     */
    get isLoadingReviews () {
        return this._isLoadingReviews;
    }

    /**
     * Sets private _isLoadingReviews
     * and updates state of the button
     * @param {boolean} value
     */
    set isLoadingReviews (value) {
        this._isLoadingReviews = value;
        if (!this.loadNextReviewsButton) return

        if (value) {
            this.loadNextReviewsButton.setAttribute('disabled', 'disabled');
            this.loadNextReviewsButton.textContent = 'Загрузка...';
        } else {
            this.loadNextReviewsButton.removeAttribute('disabled');
            this.loadNextReviewsButton.textContent = 'Больше отзывов';
        }
    }

    /**
     * Change sorting and sort
     * @param {MouseEvent|TouchEvent|PointerEvent} event
     */
    onSortReviews (event) {
        event.preventDefault();
        const predicate = event.currentTarget.dataset.sortBy;
        this.sort.reverse = false === this.sort.reverse && predicate === this.sort.predicate;
        this.sort.predicate = predicate;
        this.updateSortButtons();
        this.reloadReviews();
    }

    /**
     * Sorts reviews based on predicate
     * @param {string} predicate
     * @param {boolean} reverse order
     */
    sortReviews (predicate, reverse) {
        reverse = true === reverse ? -1 : 1;
        const sortedReviews = this.reviews.sort((a, b) => {
            // First sort based on predicate.
            // Reversing only applies to predicated sorting.
            if (a[predicate] < b[predicate]) return 1 * reverse;
            if (a[predicate] > b[predicate]) return (-1) * reverse;

            // Then on rating.
            // If predicate is 'rating', this takes no additional effect.
            if ('rating' !== predicate) {
                if (a.rating < b.rating) return 1;
                if (a.rating > b.rating) return (-1);
            }

            // Then on like count.
            if (a.likeCount < b.likeCount) return 1;
            if (a.likeCount > b.likeCount) return (-1);

            // Then on dislike count.
            if (a.dislikeCount < b.dislikeCount) return (-1);
            if (a.dislikeCount > b.dislikeCount) return 1;

            // Then on date.
            // If predicate is 'date', this takes no additional effect.
            if ('date' !== predicate) {
                if (a.date < b.date) return 1;
                if (a.date > b.date) return (-1);
            }

            return 0;
        });

        for (const review of sortedReviews) {
            // Safe to append to this.reviewsContainer
            // because if it equals null, then there are no reviews
            // and sorting will not be called ever.
            this.reviewsContainer.appendChild(review.container);
        }


    }

    /**
     * Filters reviews with photos only
     * @param {MouseEvent|TouchEvent|PointerEvent} event
     */
    onFilterHasPhotos (event) {
        event.preventDefault();

        const filter = event.currentTarget;
        this.shouldFilterReviewsWithPhotos = !!filter.checked;

        this.reloadReviews();
    }

    /**
     * Updates state of sorting filter buttons
     */
    updateSortButtons () {
        for (const btn of [...this.container.querySelectorAll('.filter-sorting [data-sort-by]')]) {
            if (this.sort.predicate === btn.dataset.sortBy) {
                btn.classList.add('is-active');
                btn.classList[this.sort.reverse ? 'add' : 'remove']('is-reverse');
            } else {
                btn.classList.remove('is-active');
                btn.classList.remove('is-reverse');
            }
        }
    }

    /**
     * Hides loadNextReviewsButton
     */
    hideLoadNextReviewsButton () {
        if (this.loadNextReviewsButton instanceof HTMLElement) {
            this.loadNextReviewsButton.style.display = 'none';
        }
    }

    /**
     * Shows loadNextReviewsButton
     */
    showLoadNextReviewsButton () {
        if (this.loadNextReviewsButton instanceof HTMLElement) {
            this.loadNextReviewsButton.style.display = '';
        }
    }

    /**
     * Loads next reviews
     * @return {Promise<void>}
     */
    async loadNextReviews () {
        const limit = this.perPageReviewCount;
        const offset = this.reviews.length ?? 0;

        try {
            const reviewsHtml = await this.getReviewsHtml(limit, offset);
            this.addReviews(reviewsHtml);
        } catch (error) {
            // No action because it's a logic exception. Exception caused by server error is logged inside getReviewsHtml
        }
    }

    /**
     * Loads next reviews
     * @return {Promise<void>}
     */
    async reloadReviews () {
        const limit = this.reviews.length || this.perPageReviewCount;

        try {
            const reviewsHtml = await this.getReviewsHtml(limit, 0);
            this.reviews = [];
            this.reviewsContainer.innerHTML = '';
            this.addReviews(reviewsHtml);
        } catch (error) {
            // No action because it's a logic exception. Exception caused by server error is logged inside getReviewsHtml
        }
    }

    /**
     * Loads reviews
     * @param {number} limit
     * @param {number} offset
     * @return {Promise<string>}
     */
    async getReviewsHtml (limit, offset) {
        if (this.isLoadingReviews)
            throw new Error('Existing request pending');

        this.lastReviewsRequestLimit = limit;

        try {
            this.isLoadingReviews = true;

            const params = {
                product_id: this.productId,
                limit,
                offset,
                with_photos: this.shouldFilterReviewsWithPhotos
            };

            if (this.sort.predicate) {
                params.sort_predicate = this.sort.predicate;
                params.sort_order = this.sort.reverse ? "ASC" : "DESC";
            }

            const queryParams = new UrlQueryParams(params);
            const response = await fetch(`${Reviews.API_FETCH_REVIEWS_URL}?${queryParams}`, {
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                },
            });

            return await response.text();
        } catch (error) {
            const message = error.message || 'Внутреняя ошибка, попробуйте позже';
            showMessage(message);
            throw new Error(message);
        } finally {
            this.isLoadingReviews = false;
        }
    }

    /**
     * Renders reviews
     * @private
     * @param {string} reviewsHtml
     * @return {void}
     */
    addReviews (reviewsHtml) {
        const tmp = document.createElement('div');
        tmp.innerHTML = reviewsHtml;

        const reviewElements = [...tmp.querySelectorAll('.review')];

        for (const reviewElement of reviewElements) {
            const review = new Review(reviewElement);

            this.reviews.push(review);
            this.reviewsContainer.appendChild(review.container);
        }

        tmp.innerHTML = '';

        if (reviewElements.length < this.lastReviewsRequestLimit) {
            this.hideLoadNextReviewsButton();
        } else if (this.loadNextReviewsButton instanceof HTMLElement) {
            // Make sure the .js-show-more-reviews is the last element
            this.showLoadNextReviewsButton();
            this.reviewsContainer.appendChild(this.loadNextReviewsButton);
        }
    }

    /**
     * Attaches event listeners
     */
    attachEventListeners () {
        // Init sorting
        for (const sortButton of [...this.container.querySelectorAll('.filter-sorting [data-sort-by]')]) {
            sortButton.addEventListener('click', this.listeners.onSortReviews);
        }
        const filertHasPhotos = this.container.querySelector('input[name="filter-has-photos"]');
        if (filertHasPhotos) {
            filertHasPhotos.addEventListener('change', this.listeners.onFilterHasPhotos);
        }

        if (this.loadNextReviewsButton instanceof HTMLElement) {
            this.loadNextReviewsButton.addEventListener('click', this.listeners.loadNextReviews);
        }
    }
}
